import { IonText } from "@ionic/react";
import { RefObject, useCallback, useMemo, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import PrimaryButton from "@components/buttons/PrimaryButton";
import DateAndTimeRange from "@components/dates/DateAndTimeRange";
import MedicationForm from "@components/forms/MedicationForm";
import MedicationStep from "@components/MedicationStep";
import Modal, { ModalElement } from "@components/Modal";
import ScheduleConsultButton from "@components/ScheduleConsultButton";
import ButtonLink from "@components/typography/ButtonLink";
import useOpener from "@hooks/useOpener";
import styles from "@theme/assessment.module.css";
import {
  AssessmentStepSkipReasonEnum,
  AssessmentStepTypeEnum,
  ConsultFragment as Consult,
  GetJourneyWithCareProfileQuery,
  MedicationFragment as Medication,
  MedicationKindEnum
} from "@typing/Generated";
import { MedicationGroups } from "@typing/Models";
import { imageUrl } from "@utils/imageUtils";
import { consultEndsInFuture } from "@utils/timeUtils";

type AssessmentStep = GetJourneyWithCareProfileQuery["journey"]["assessments"][number]["steps"][number];

type Props = {
  backAllowed: boolean;
  batchedMedications: MedicationGroups;
  consults: Consult[];
  handleBack: () => void;
  handleOpenMessages: () => void;
  handleSkipReasonToggle: (reason: AssessmentStepSkipReasonEnum) => void;
  journeyId: string;
  nextAllowed: boolean;
  onUpdate: () => void;
  question: AssessmentStep | undefined;
  skipReason: AssessmentStepSkipReasonEnum | null;
};

const MedicationQuestion = ({
  backAllowed,
  batchedMedications,
  consults,
  handleBack,
  handleOpenMessages,
  handleSkipReasonToggle,
  journeyId,
  nextAllowed,
  onUpdate,
  question,
  skipReason
}: Props) => {
  const intl = useIntl();
  const [editingMedication, setEditingMedication] = useState<Medication | undefined>(undefined);
  const prescriptionOpener = useOpener();
  const overTheCounterOpener = useOpener();
  const vitaminsAndSupplementsOpener = useOpener();

  const prescriptionModalRef = useRef<ModalElement>(null);
  const overTheCounterModalRef = useRef<ModalElement>(null);
  const vitaminsAndSupplementsModalRef = useRef<ModalElement>(null);

  const generateErrorHandler = useCallback(
    (ref: RefObject<ModalElement>) => () => {
      if (ref.current) {
        ref.current.scrollToTop();
      }
    },
    []
  );

  const handleCreateMedication = useCallback(
    (step: AssessmentStepTypeEnum) => {
      setEditingMedication(undefined);
      if (step === AssessmentStepTypeEnum.prescriptions) {
        prescriptionOpener.open();
      } else if (step === AssessmentStepTypeEnum.over_the_counter) {
        overTheCounterOpener.open();
      } else {
        vitaminsAndSupplementsOpener.open();
      }
    },
    [overTheCounterOpener, prescriptionOpener, vitaminsAndSupplementsOpener]
  );

  const handleEditMedication = useCallback(
    (medication: Medication) => {
      setEditingMedication(medication);
      if (medication.kind === MedicationKindEnum.prescription) {
        prescriptionOpener.open();
      } else if (medication.kind === MedicationKindEnum.over_the_counter) {
        overTheCounterOpener.open();
      } else {
        vitaminsAndSupplementsOpener.open();
      }
    },
    [overTheCounterOpener, prescriptionOpener, vitaminsAndSupplementsOpener]
  );

  const currentQuestionInformation = useMemo(() => {
    if (question?.step === AssessmentStepTypeEnum.prescriptions) {
      return {
        kind: MedicationKindEnum.prescription,
        medications: batchedMedications.prescription,
        modalRef: prescriptionModalRef,
        opener: prescriptionOpener
      };
    }
    if (question?.step === AssessmentStepTypeEnum.over_the_counter) {
      return {
        kind: MedicationKindEnum.over_the_counter,
        medications: batchedMedications.overTheCounter,
        modalRef: overTheCounterModalRef,
        opener: overTheCounterOpener
      };
    }
    return {
      kind: MedicationKindEnum.vitamin_or_supplement,
      medications: batchedMedications.vitaminAndSupplement,
      modalRef: vitaminsAndSupplementsModalRef,
      opener: vitaminsAndSupplementsOpener
    };
  }, [
    batchedMedications,
    overTheCounterModalRef,
    prescriptionModalRef,
    vitaminsAndSupplementsModalRef,
    question?.step,
    prescriptionOpener,
    vitaminsAndSupplementsOpener,
    overTheCounterOpener
  ]);

  if (question) {
    if (question.step === AssessmentStepTypeEnum.start) {
      return (
        <div className={styles.content}>
          <div className={styles.intro}>
            <div className={styles.introImg}>
              <img
                alt={intl.formatMessage({ id: "accessibility.altTags.medicationsListIncomplete" })}
                src={imageUrl("medicalIncomplete")}
              />
            </div>
            <div className={styles.title} data-test="why-it-matters">
              <IonText>
                <FormattedMessage id="pages.client.medications.sections.whyItMatters" />
              </IonText>
            </div>
            <div className={styles.description}>
              <IonText>
                <FormattedMessage id="pages.client.medications.sections.instructionalContent" />
              </IonText>
            </div>
            <PrimaryButton
              data-test="get-started"
              expand="block"
              onClick={onUpdate}
              textKey="pages.client.medications.sections.getStarted"
            />
          </div>
        </div>
      );
    }
    return (
      <div className={styles.content}>
        <div className={styles.step}>
          <MedicationStep
            backAllowed={backAllowed}
            handleBack={handleBack}
            handleSkipReasonToggle={handleSkipReasonToggle}
            journeyId={journeyId}
            medications={currentQuestionInformation.medications}
            nextAllowed={nextAllowed}
            onCreate={handleCreateMedication}
            onEdit={handleEditMedication}
            onUpdate={onUpdate}
            question={question}
            skipReason={skipReason}
          />
        </div>

        <Modal
          opener={currentQuestionInformation.opener}
          ref={currentQuestionInformation.modalRef}
          title={
            editingMedication
              ? intl.formatMessage({ id: `models.client.medication.edit${currentQuestionInformation.kind}` })
              : intl.formatMessage({ id: `models.client.medication.add${currentQuestionInformation.kind}` })
          }
        >
          {({ modalDisplayed }) => (
            <MedicationForm
              autofocus={modalDisplayed}
              journeyId={journeyId}
              kind={currentQuestionInformation.kind}
              medication={editingMedication}
              onCancel={currentQuestionInformation.opener.close}
              onError={generateErrorHandler(currentQuestionInformation.modalRef)}
              onSuccess={currentQuestionInformation.opener.close}
            />
          )}
        </Modal>
      </div>
    );
  }

  return (
    <div className={styles.content}>
      <div className={styles.conclusion}>
        <div className={styles.introImg}>
          <img
            alt={intl.formatMessage({ id: "accessibility.altTags.medicationsListComplete" })}
            src={imageUrl("medicalComplete")}
          />
        </div>
        <div className={styles.title}>
          <IonText>
            <FormattedMessage id="pages.client.medications.review.thankYou" />
          </IonText>
        </div>
        <div className={styles.description}>
          <div className="ion-margin-bottom">
            <IonText>
              <FormattedMessage id="pages.client.medications.review.remember" />
            </IonText>
          </div>
          <IonText>
            <FormattedMessage
              id="pages.client.medications.review.expertReview"
              values={{
                here: (
                  <ScheduleConsultButton
                    isRouterLink
                    journeyId={journeyId}
                    messageKey="pages.client.medications.review.here"
                  />
                ),
                sendMessage: (
                  <ButtonLink onClick={handleOpenMessages}>
                    <FormattedMessage id="pages.client.medications.review.sendMessage" />
                  </ButtonLink>
                )
              }}
            />
          </IonText>
        </div>
        {consults.map(
          consult =>
            consultEndsInFuture(consult) && (
              <div className={styles.consult} key={consult.id}>
                <div>
                  <IonText className={styles.consultName}>
                    <FormattedMessage id="models.consult.genericName" />
                  </IonText>
                </div>
                <div>
                  <IonText>
                    <DateAndTimeRange endTime={consult.endTime} startTime={consult.startTime} />
                  </IonText>
                </div>
                <div>
                  <IonText>
                    <FormattedMessage
                      id="pages.client.medications.review.expert"
                      values={{ firstName: consult.expert.firstName }}
                    />
                  </IonText>
                </div>
              </div>
            )
        )}
        <PrimaryButton
          data-test="complete"
          expand="block"
          onClick={onUpdate}
          textKey="pages.client.medications.review.close"
        />
      </div>
    </div>
  );
};

export default MedicationQuestion;
